// 공유 계약(소유: 오케스트레이터). 게임 로직(src/core, src/game)이 쓰고 렌더·UI(src/render, src/vfx, src/ui)는 읽기만 한다.
// 렌더는 GameView를 매 프레임 읽고 EventQueue를 자기 커서로 소비한다. 게임 로직은 three를 import하지 않는다.

import type { EventQueue } from './events';
import type { RebirthAlloc, RebirthView } from './rebirth';
import type { StatAlloc, StatView } from './stats';
import type { MasteryAlloc, MasteryView } from './mastery';
import type { MapDef, MapGrid, RegionId } from './levels';
import type { ItemSlot } from './rpg';
import type { EquipSlot } from './content/types';
import type { GearView, LootView } from './gearView';
import type { GrowthCode } from './growth';
import type { SlotsView } from './skillSlots';
import type { TrialBoardId, TrialsView } from './content/trials';
import type { JobView } from './content/jobQuest';
import type { FieldView } from './field/view';
import type { PartyView } from './partyView';
import type { RaidNetView } from './raidNetView';
import type { AvatarView } from './avatar';
import type { AdventureView } from './adventure';
import type { CodexView, WorldRuleView } from './codexView';
export type { RegionId, MapId } from './levels';

// ── 입력 ─────────────────────────────────────────────
export const Btn = {
  Attack: 1 << 0,
  Dodge: 1 << 1,
  Skill1: 1 << 2,
  Skill2: 1 << 3,
  Skill3: 1 << 4,
  Skill4: 1 << 5,
  Awaken: 1 << 6,
  Interact: 1 << 7,
  Pause: 1 << 8,
  /** 천공 올려베기(런처, Lv5 해금). 2026-10-22 c005 launcher */
  Skill5: 1 << 9,
  /** 물약 퀵슬롯(D2c): 1번 = HP(키 R), 2번 = MP(키 T). 던전에서만, 공용 쿨다운 6초 */
  Potion1: 1 << 10,
  Potion2: 1 << 11,
  /** 스킬 6..13(c022 라이라: 스킬이 5개를 넘는 영웅). 키는 슬롯(1~6·Alt)이 정하고 옛 U·I·O·L·P 키는 스킬 1..5 그대로 */
  Skill6: 1 << 12,
  Skill7: 1 << 13,
  Skill8: 1 << 14,
  Skill9: 1 << 15,
  Skill10: 1 << 16,
  Skill11: 1 << 17,
  Skill12: 1 << 18,
  /** 스킬 13(라이라 전직 II). 비트 19 */
  Skill13: 1 << 19,
} as const;
export type BtnBit = (typeof Btn)[keyof typeof Btn];
/** 스킬 i+1의 입력 비트(인덱스 0..4). `Btn.Skill1 << i`는 5번째에서 각성 비트와 겹치므로 이 표를 쓴다 */
export const SKILL_BTN = [Btn.Skill1, Btn.Skill2, Btn.Skill3, Btn.Skill4, Btn.Skill5] as const;
/** 모든 스킬 번호 1..SKILL_MAX의 입력 비트(c022). 카엘은 앞 5개만 쓰고, 라이라처럼 스킬이 많은 영웅이 뒤를 쓴다 */
export const SKILL_BTN_ALL = [...SKILL_BTN, Btn.Skill6, Btn.Skill7, Btn.Skill8, Btn.Skill9, Btn.Skill10, Btn.Skill11, Btn.Skill12, Btn.Skill13] as const;
/** 한 영웅이 가질 수 있는 스킬 번호 상한(12 → 13, 라이라 전직 I·II). 슬롯 12칸 + 환생 추가 칸 2보다 작아 모두 놓을 수 있다. 쿨다운·랭크 배열 길이 */
export const SKILL_MAX = 13;
/** 플레이할 수 있는 영웅(c022: 카엘·라이라). 콘텐츠 표(content/types HeroId)의 부분집합 */
export type PlayableHeroId = 'kael' | 'lyra';
export const PLAYABLE_HEROES: readonly PlayableHeroId[] = ['kael', 'lyra'];

/**
 * 시뮬레이션 1틱에 전달되는 입력. 이동은 언제나 월드 축이다.
 * 카메라가 돌면 main.ts가 shared/cameraYaw.screenToWorld로 화면 축 입력을 월드 축으로 돌려 넣는다.
 */
export interface InputFrame {
  /** -1..1, 월드 +x(카메라 yaw 0에서 화면 오른쪽) */
  moveX: number;
  /** -1..1, 월드 +z(카메라 yaw 0에서 화면 아래, W는 -z) */
  moveZ: number;
  /** 누르고 있는 버튼 비트 */
  held: number;
  /** 지난 틱 이후 새로 눌린 버튼 비트(한 번만 전달) */
  pressed: number;
}

/** 터치 UI(src/ui)가 입력을 넣는 창구. 키보드·게임패드는 src/core/input.ts가 직접 처리한다. */
export interface InputSink {
  setStick(x: number, z: number): void;
  buttonDown(btn: BtnBit): void;
  buttonUp(btn: BtnBit): void;
  /** 자동 퀘스트 이동(화면 축). 키보드·조이스틱·패드 이동이 있으면 그쪽이 이긴다 */
  setAuto?(x: number, z: number): void;
  /** 사용자가 지금 직접 이동 입력 중인지(키·조이스틱·패드) */
  manualMove?(): boolean;
}

// ── 상태 열거 ─────────────────────────────────────────
/** Field·Rift(레인 F, ARPG 모드): 끝에 붙여 기존 번호를 유지한다(REBIRTH-MODES-SPEC 3장) */
export const Scene = { Title: 0, Village: 1, Dungeon: 2, Result: 3, Defeat: 4, Field: 5, Rift: 6 } as const;
export type SceneId = (typeof Scene)[keyof typeof Scene];
/** 싸우는 장면(던전·필드·균열): 전투·드롭·물약·보스가 도는 곳 */
export function combatScene(s: number): boolean { return s === Scene.Dungeon || s === Scene.Field || s === Scene.Rift; }

export const HeroAct = { Idle: 0, Run: 1, Attack: 2, Dodge: 3, Skill: 4, Awaken: 5, Hurt: 6, Dead: 7, Victory: 8 } as const;
export type HeroActId = (typeof HeroAct)[keyof typeof HeroAct];

/** Shaman(2026-10-15 R2): 후열 원거리 포격 몹(버섯 주술사 GLB). WaveDef.counts 4번째 칸 */
export const MobKind = { Goblin: 0, ShieldGoblin: 1, Boar: 2, Shaman: 3 } as const;
export type MobKindId = (typeof MobKind)[keyof typeof MobKind];
export const MOB_KIND_COUNT = 4;

/** 피격 상태 5종(COMBAT-FEEL 2.7): 서기 = Idle/Chase, 경직 = Stunned, 띄움 = Airborne, 다운 = Down, 기상 = Wakeup(무적). Dying 뒤에 추가해 기존 번호를 유지한다 */
export const MobState = { Inactive: 0, Spawning: 1, Idle: 2, Chase: 3, Windup: 4, Attack: 5, Stunned: 6, Airborne: 7, Dying: 8, Down: 9, Wakeup: 10 } as const;
export type MobStateId = (typeof MobState)[keyof typeof MobState];

/**
 * 보스 상태. 잿불 대주술사(BossKind.Ember)는 같은 상태를 다른 패턴으로 쓴다:
 * SlamWindup/Slam = 자기 둘레 화염 고리, SweepWindup/Sweep = 부채꼴 균열 3줄, Cast = 운석 연속 낙하(경고 원이 차례로 생긴다).
 */
export const BossState = { Inactive: 0, Intro: 1, Idle: 2, Chase: 3, SlamWindup: 4, Slam: 5, SweepWindup: 6, Sweep: 7, Summon: 8, Stagger: 9, Dying: 10, Dead: 11, Cast: 12 } as const;
export type BossStateId = (typeof BossState)[keyof typeof BossState];
/** 보스 종류. Ember 렌더는 전용 모델 boss_ember_archmage.glb(없으면 버섯 주술사 확대판, docs/HUNT-R2.md 6절) */
/** 2 = 잿불 비룡(레이드 1, RAID-PRD) — 행동은 game/raid.ts, 렌더는 render/wyvern.ts */
export const BossKind = { Treant: 0, Ember: 1, Wyvern: 2 } as const;
export type BossKindId = (typeof BossKind)[keyof typeof BossKind];

export const AwakenPhase = { None: 0, Gather: 1, Flash: 2, Cutin: 3, Barrage: 4, Recover: 5 } as const;
export type AwakenPhaseId = (typeof AwakenPhase)[keyof typeof AwakenPhase];

/** 공격 단계: 선딜 / 판정 / 후딜 */
export const AttackPhase = { Windup: 0, Active: 1, Recovery: 2 } as const;
export type AttackPhaseId = (typeof AttackPhase)[keyof typeof AttackPhase];

// ── 읽기 전용 뷰 ─────────────────────────────────────
export interface HeroView {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly prevX: number;
  readonly prevY: number;
  readonly prevZ: number;
  /** 바라보는 방향(rad). atan2(dirX, dirZ): 0 = +z(화면 아래), π = -z(화면 위) */
  readonly facing: number;
  /** D3a 지역 상태 이상 남은 틱(서리 둔화·중독). HUD 가장자리 표시용 */
  readonly chillTicks: number;
  readonly poisonTicks: number;
  readonly act: HeroActId;
  /** 현재 동작 시작 후 경과 틱 */
  readonly actTick: number;
  /** Attack일 때 0..3 */
  readonly comboStep: number;
  readonly attackPhase: AttackPhaseId;
  /** Skill일 때 1..SKILL_MAX(카엘 1..5) */
  readonly skillId: number;
  readonly hp: number;
  readonly maxHp: number;
  readonly mp: number;
  readonly maxMp: number;
  /** 스킬 MP 소모 감소 비율(유니크). 실제 소모 = shared/gear effectiveMpCost(mpCost, mpCostCut) */
  readonly mpCostCut: number;
  readonly level: number;
  readonly invulnerable: boolean;
  /** 스킬 1..SKILL_MAX의 남은 쿨다운 틱(인덱스 0..12, 카엘은 0..4만 쓴다) */
  readonly cooldowns: Float32Array;
  readonly cooldownMax: Float32Array;
  readonly dodgeCooldown: number;
  /** 회피 캔슬 쿨 남은 틱. 0이면 공격·스킬 도중에도 회피로 끊을 수 있다 */
  readonly dodgeCancelCooldown: number;
  /** 0..1 */
  readonly awakenGauge: number;
}

/** 몹은 고정 용량 SoA 배열. state === Inactive인 슬롯은 비어 있다. */
export interface MobsView {
  readonly capacity: number;
  /** state가 Inactive가 아닌 슬롯 수(Spawning·Dying 포함). 렌더는 capacity 전체를 돌며 종류별로 압축한다 */
  readonly aliveCount: number;
  readonly state: Uint8Array;
  readonly kind: Uint8Array;
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly z: Float32Array;
  readonly prevX: Float32Array;
  readonly prevY: Float32Array;
  readonly prevZ: Float32Array;
  readonly facing: Float32Array;
  readonly hp: Float32Array;
  readonly maxHp: Float32Array;
  /** 현재 상태 경과 틱 */
  readonly stateTick: Uint16Array;
  /** 마지막 피격 후 경과 틱. 0 = 이번 틱 피격, 0xffff = 피격 없음 */
  readonly hitTick: Uint16Array;
  /** 엘리트 여부(크기·체력 강화) */
  readonly elite: Uint8Array;
  /** 자세 게이지 0..1. 방패 고블린·엘리트만 사용한다 */
  readonly posture: Float32Array;
  /** 가드 브레이크 뒤 무방비로 남은 틱. 0 = 정상 */
  readonly guardBroken: Uint16Array;
  /** 이번 선딜 길이(틱, Windup 진입 때 기록). 지역 공세로 짧아진 선딜에 렌더가 자세를 맞춘다(c018). 0 = 기본값(MOB_STATS.windup) */
  readonly windupTicks: Uint16Array;
  /** 슬롯 세대. 스폰할 때마다 1 증가(렌더가 슬롯 재사용을 감지해 보간·트레일을 끊는다) */
  readonly gen: Uint16Array;
  /** 속박 남은 틱(c022 그물 덫, 0 = 없음). 렌더는 그물 표시에 쓴다. 옛 픽스처에는 없다 */
  readonly rooted?: Uint16Array;
  /** 스페셜 몬스터 표지(shared/specialMonster MobSpecial: 0 일반, 1 황금 고블린, 2 챔피언). 옛 픽스처에는 없다 */
  readonly special?: Uint8Array;
  /** 챔피언 접두어 번호(CHAMPION_AFFIXES 인덱스) */
  readonly affix?: Uint8Array;
  /** C2 몹 직군(shared/mobRoles MobRole). 옛 픽스처에는 없다 */
  readonly role?: Uint8Array;
  /** C2 정예 접두(shared/mobRoles EliteAffix, elite=1일 때만). 옛 픽스처에는 없다 */
  readonly eliteAffix?: Uint8Array;
  /** C2 보호막 정예의 남은 보호막 0..1(0 = 없음·깨짐) */
  readonly shieldFrac?: Float32Array;
}

/**
 * 스페셜 몬스터 한 판 상태(STAGE-WORLD-PRD 4절). 알림 띠·이름표·오라가 읽는다. 옛 픽스처에는 없다.
 * phase: 0 아직(나올 계획이 없거나 아직 안 나옴), 1 출현 중, 2 끝(처치·소멸)
 */
export interface SpecialView {
  readonly phase: number;
  /** MobSpecial 번호(출현 중·끝일 때만 0이 아니다) */
  readonly kind: number;
  /** 몹 슬롯(출현 중일 때만 유효, 아니면 -1) */
  readonly mob: number;
  /** 황금 고블린이 맞아서 도망 중인지 */
  readonly fleeing: boolean;
  /** 황금 고블린 남은 틱(세계 시간, 맞기 전엔 등장 대기 시간, 맞은 뒤엔 도망 시간) */
  readonly remainTicks: number;
  /** 챔피언 접두어 번호 */
  readonly affix: number;
  /** 챔피언 이름표(「접두어 몹이름」), 황금 고블린은 '황금 고블린' */
  readonly name: string;
  /** 출현할 때마다 1 증가(알림 띠가 새 출현을 알아챈다) */
  readonly serial: number;
  /** 알림 띠를 더 보일 틱(챔피언은 출현 뒤 몇 초) */
  readonly bannerTicks: number;
  /** 이번 판 계획의 등장 방(판 계획 방 인덱스: 황금 고블린 = 나올 전투방, 챔피언 = 마지막 전투방). 계획이 없으면 -1. QA가 그 방으로 이동하는 데 쓴다 */
  readonly planRoom: number;
  /** 마지막 처치 보상(골드는 기본 보상 단위, 강화석 개수)과 보상 번호(처치마다 1 증가) */
  readonly rewardGold: number;
  readonly rewardStones: number;
  readonly rewardSerial: number;
}

export interface BossView {
  readonly kind: BossKindId;
  /** D3a 보스 정의 번호(content/bosses BOSS_KEYS 순서): 렌더가 원형 모델에 크기·색조를 입힌다 */
  readonly variant: number;
  /** 페이즈 2 진입 여부 */
  readonly phase2: boolean;
  /** S5: 0 없음, 1 분노(n-5 중간보스), 2 광폭(n-10 지역보스 페이즈 3) */
  readonly rage?: number;
  /** S5: 분노·광폭 체력 문턱(최대 체력 비율, 0 = 없음) — 보스 체력바 눈금 */
  readonly rageAt?: number;
  readonly state: BossStateId;
  readonly stateTick: number;
  readonly x: number;
  readonly z: number;
  readonly prevX: number;
  readonly prevZ: number;
  readonly facing: number;
  readonly hp: number;
  readonly maxHp: number;
  readonly radius: number;
  readonly hitTick: number;
  readonly name: string;
  /** 등장 연출 중 무적 등 */
  readonly invulnerable: boolean;
  /** 경직 게이지 0..1(1에서 Stagger) */
  readonly stagger: number;
  /** 비행 높이 비율 0..1(레이드 비룡 페이즈 2, 0 = 지상). 공중에서는 피해를 받지 않는다 */
  readonly air?: number;
}

/** 바닥 경고 데칼. 주황 원 → 발동 0.5초 전부터 빨강(AV-SPEC). */
export const TeleShape = { Circle: 0, Cone: 1, Line: 2 } as const;
export type TeleShapeId = (typeof TeleShape)[keyof typeof TeleShape];
export interface TelegraphSlot {
  readonly active: boolean;
  readonly shape: TeleShapeId;
  readonly x: number;
  readonly z: number;
  /** Circle/Cone: 반경, Line: 길이 */
  readonly radius: number;
  /** Cone/Line: 방향(rad, facing 규약과 같음) */
  readonly angle: number;
  /** Cone: 전체 각(rad), Line: 폭(m) */
  readonly width: number;
  /** 경과 틱 / 전체 틱 */
  readonly tick: number;
  readonly total: number;
}
export const TELEGRAPH_CAPACITY = 24;

export interface FlowView {
  readonly scene: SceneId;
  readonly sceneTick: number;
  readonly mapId: MapDef['id'];
  /** 현재 방 인덱스(map.rooms), 방 밖이면 -1 */
  readonly roomIndex: number;
  /** 각 방 상태: 0 대기, 1 전투 중, 2 정리됨 */
  readonly roomState: Uint8Array;
  readonly wave: number;
  readonly waveCount: number;
  readonly objectiveKilled: number;
  readonly objectiveTotal: number;
  /** map.gates 순서. 0 닫힘 ~ 1 열림(애니메이션 포함) */
  readonly gateOpen: Float32Array;
  readonly stageLabel: string;
  readonly objectiveLabel: string;
  /** 던전 입장 후 경과 틱(일시정지 제외) */
  readonly elapsedTicks: number;
  readonly paused: boolean;
  /** loadMap/retry마다 1 증가. 렌더는 바뀌면 VFX 풀을 비우고 카메라를 즉시 맞춘다 */
  readonly sceneSerial: number;
  /** 지금 판의 스테이지 id('r3-s05'). 마을·자유 입장이면 '' (D3a) */
  readonly stageId: string;
  /** 지금 판 종류: normal·mid(중간보스)·final(지역보스)·free(자유 입장)·''(마을) */
  readonly stageKind: string;
  /** 지금 마을 번호(1..4). 마을 지도는 levels.villageOf(town) */
  readonly town: number;
  /**
   * 마을 이동 연출(n-10 첫 클리어 뒤·워프): 도착 마을 번호(0 = 없음)와 남은 틱. 연출 동안 영웅 입력은 막히고 화면 가장자리에 지역 제목 카드가 뜬다.
   * travelFrom = 떠난 마을 번호
   */
  readonly travelTo: number;
  readonly travelFrom: number;
  readonly travelTick: number;
  readonly travelTotal: number;
  /** 클리어 연출 대기(마지막 적 처치 장면): 남은 틱(0 = 아님)·전체 틱·카메라가 볼 처치 위치. 대기 중에는 영웅 조작이 그대로 된다 */
  readonly clearHold: number;
  readonly clearHoldTotal: number;
  readonly clearFocusX: number;
  readonly clearFocusZ: number;
}

/** 스테이지 진행(D3a, dungeonhero.stage.v1). 1-1 … 4-10 순차 해금 */
export interface StageProgressView {
  /** 차례로 깬 스테이지 수(0..40) */
  readonly cleared: number;
  /** 지금 마을, 도착한 가장 높은 마을 */
  readonly town: number;
  readonly highestTown: number;
  /** 지금 도전할 스테이지 id */
  readonly frontier: string;
  /** 4-10까지 다 깼는지 */
  readonly complete: boolean;
  readonly revision: number;
  /** 그 지역에 들어갈 스테이지(고른 것 또는 최전선). 지역이 잠겼으면 null */
  selected(region: RegionId): string | null;
  unlocked(id: string): boolean;
  regionUnlocked(region: RegionId): boolean;
  stageClears(id: string): number;
  stageStars(id: string): number;
}

/** 하드 난이도 진행(D3b, dungeonhero.stage.hard.v1) */
export interface HardProgressView {
  /** 차례로 깬 하드 스테이지 수(0..40) */
  readonly cleared: number;
  /** 마을 문 난이도가 하드인지 */
  readonly on: boolean;
  /** 하드 1지역이 열렸는지(노말 1-10 클리어) */
  readonly available: boolean;
  readonly complete: boolean;
  readonly revision: number;
  /** 그 지역에 들어갈 하드 스테이지(h…). 하드 지역이 잠겼으면 null */
  selected(region: RegionId): string | null;
  unlocked(id: string): boolean;
  regionUnlocked(region: RegionId): boolean;
  stageClears(id: string): number;
  stageStars(id: string): number;
}

export interface ComboView {
  readonly count: number;
  /** 초기화까지 남은 틱 */
  readonly timer: number;
  readonly best: number;
  /** 마지막으로 도달한 칭찬 단계(PRAISE_TIERS 인덱스 + 1, 0 = 없음) */
  readonly praiseTier: number;
}

/** 스킬 연계(LINK) 상태. sim이 정하고 UI는 읽기만 한다(설계도 rule-skill-link). */
export interface LinkView {
  /** 0 = 연계 없음, 1..3 = 이번 연계에서 쓴 서로 다른 스킬 수 */
  readonly stage: number;
  /** 다음 스킬을 이어 붙일 수 있는 남은 틱. 스킬을 시작하면 LINK.window로 채우고 사용 중에는 줄지 않는다 */
  readonly timer: number;
  /** 이번 연계에서 쓴 스킬 비트(bit i = 스킬 i+1) */
  readonly used: number;
  /** 지금 누르면 현재 스킬을 캔슬하고 나가는 스킬 비트(bit i = 스킬 i+1). 쿨·MP·캔슬 시점을 모두 반영한다 */
  readonly cancelReady: number;
  /** 이번 판 3단 피니시 횟수 */
  readonly finishes: number;
  /** 이번 판 최고 단계 */
  readonly best: number;
}

export interface AwakenView {
  readonly phase: AwakenPhaseId;
  readonly phaseTick: number;
  /** 현재 단계 전체 틱(컷인 생략 시 Cutin=0) */
  readonly phaseTotal: number;
  /** Barrage 중 지금까지 나간 참격 수 */
  readonly slashIndex: number;
  readonly usedThisRun: number;
}

export interface ResultView {
  /** 현재 결과의 영속 ID. 전투 중에는 빈 문자열 */
  readonly resultId: string;
  /** 이번 화면이 저장된 미선택 결과를 복원한 것인지 */
  readonly restored: boolean;
  /** 저장 대기·실패로 확정되지 않은 보상이 있는지 */
  readonly savePending: boolean;
  /** 손상된 이전 저장을 백업하고 이 탭의 마지막 정상 상태로 새로 이어 쓴 적이 있는지(결과 화면 안내) */
  readonly saveRecovered: boolean;
  readonly cleared: boolean;
  readonly stars: number;
  /** 별 사유 비트: 1 클리어, 2 5분 이내, 4 남은 HP 50% 이상 */
  readonly starMask: number;
  readonly clearTicks: number;
  readonly kills: number;
  readonly maxCombo: number;
  readonly damageDealt: number;
  readonly gold: number;
  /** 카드 보상 3장의 골드 액수. 결과 화면 전에는 -1 */
  readonly cards: Int32Array;
  /** 카드 3장의 내용 문자열(content/cardReward 형식). 골드가 아닌 카드는 cards[i] = 0. 이전 영수증은 모두 '' = 골드 카드 */
  readonly cardLoot: readonly string[];
  /** 고른 카드 인덱스(0..2). 고르기 전 -1 */
  readonly pickedCard: number;
  /** D3a: 깬 스테이지 id(자유 입장은 빈 문자열), 이번이 첫 클리어인지, 열린 다음 스테이지(빈 문자열 = 없음), 예약된 마을 이동(0 = 없음) */
  readonly stageId: string;
  readonly firstClear: boolean;
  readonly nextStage: string;
  readonly travelTo: number;
  /** S3: 이번 판 클리어 골드·카드 배율(자유 입장 1), 첫 클리어 보상(골드는 gold에 합쳐져 있음, 강화석 개수·등급, 받은 장비 '등급:정의 id') */
  readonly clearMult: number;
  readonly firstGold: number;
  readonly firstStones: number;
  readonly firstStoneTier: number;
  readonly firstGear: readonly string[];
  /** 이번 판 스페셜 몬스터 처치 골드(처치 순간 영속 지급, gold에 들어 있지 않다) */
  readonly specialGold: number;
  /**
   * 레이드(RAID-PRD 6절): 깬 레이드 id(레이드 판이 아니면 ''), 이번 주 몇 번째 보상(1..한도, 0 = 연습 — 보상 없음)·한도,
   * 받은 세트 부위·전설 정의 id('' = 없음), 강화석 수·등급, 보호 주문서 수. 골드는 gold에 들어 있다
   */
  readonly raid?: string;
  readonly raidN?: number;
  readonly raidLimit?: number;
  /** 이 판 뒤 이번 주 남은 보상 횟수(C1a — 솔로 영수증·넷 판을 합친 장부 기준, -1 = 모름) */
  readonly raidLeft?: number;
  readonly raidSet?: string;
  readonly raidLegend?: string;
  readonly raidStones?: number;
  readonly raidStoneTier?: number;
  readonly raidProtect?: number;
  /** 레이드 난이도('normal'|'hard')와 하드 첫 클리어 보상(이번 판에 받았으면 raidFirst, 골드·전설 정의·강화석 수 — 골드는 gold에 들어 있지 않다) */
  readonly raidDiff?: string;
  readonly raidFirst?: boolean;
  readonly raidFirstGold?: number;
  readonly raidFirstLegend?: string;
  readonly raidFirstStones?: number;
  /**
   * 일일 던전·무한의 탑 결과(L9a): 종류('vault'|'mine'|'tower', 일일 판이 아니면 ''), 탑 층(그 판의 층), 다음 층(탑을 이어 올라갈 수 있으면 1..50, 아니면 0 —
   * 이어가기는 결과 화면에서 enterDaily 'tower'), 탑 최고 층, 받은 강화석·보호 주문서·젬(젬은 서버 지급 전 '보관'). 골드는 gold에 들어 있다. 실패는 cleared=false
   */
  readonly dailyKind?: string;
  readonly dailyFloor?: number;
  readonly dailyNext?: number;
  readonly dailyBest?: number;
  readonly dailyStones?: number;
  readonly dailyProtect?: number;
  readonly dailyGems?: number;
  /** 넷 판 결과 안내(보상이 우편함으로 가는 경우의 한 줄, 없으면 ''). 있으면 결과 화면이 카드·로컬 저장 문구 대신 이것을 보인다 */
  readonly raidNote?: string;
  /**
   * 용사의 시련 판(3개월 C7, game/trialRun.ts): 시련 id(시련 판이 아니면 ''), 조건을 채웠나, 이번에 처음 이뤄 보상을 받았나, 판 중 최저 HP %,
   * 받은 강화석 수·등급(골드는 gold), 이번으로 시련판 10개를 모두 이뤘으면 그 시련판 id(상자는 퀘스트 창에서 연다). 실패·패배는 cleared=false
   */
  readonly trialId?: string;
  readonly trialMet?: boolean;
  readonly trialNew?: boolean;
  readonly trialLow?: number;
  readonly trialStones?: number;
  readonly trialStoneTier?: number;
  readonly trialChest?: string;
  /** 전직 관문(C10-5): 이번 판으로 그 단계 관문 3개를 모두 넘었으면 단계(1·2) — 교관에게 가서 갈래 선택·승급. 아니면 0 */
  readonly jobReady?: number;
}

export interface SettingsView {
  /** 성인(18+) 연출. 기본 false(ART-BIBLE 6장) */
  readonly mature: boolean;
  /** 섬광 약화 */
  readonly reduceFlash: boolean;
  /** 두 번째 각성부터 컷인 생략 */
  readonly skipCutin: boolean;
}

/**
 * 지역별 기록(v1 저장의 선택 필드 regions에서 읽는다, docs/HUNT-R2.md 5절).
 * r1.bestClearTicks는 기존 최상위 bestClearTicks 그대로다. clears는 이 버전부터 센다(이전 기록은 0).
 */
export interface RegionRecordView {
  readonly clears: number;
  /** 최단 클리어 틱, 없으면 0 */
  readonly bestClearTicks: number;
  /** 최고 별 0..3 */
  readonly bestStars: number;
}

// ── RPG-1: 오프라인 프로필·장비·마을 NPC (docs/STUDIO-PLAY-CONTRACT.md) ──
/** 거래·장착 명령 결과 코드. UI는 이 코드로만 문구·효과음을 고른다 */
export type TxCode =
  | 'ok'
  | 'insufficient' // 골드 부족
  | 'owned' // 이미 가짐
  | 'not_owned' // 갖지 않은 장비 장착
  | 'unknown_item'
  | 'slot_mismatch'
  | 'wrong_npc' // 열린 NPC가 파는 물건이 아님
  | 'no_menu' // NPC 창이 열려 있지 않음(마을 밖·던전·결과·패배 포함)
  | 'paused'
  | 'readonly' // 프로필 손상·미래 버전·저장소 없음
  | 'save_failed' // 저장 실패: 상태를 바꾸지 않았다
  | 'duplicate' // 같은 requestId 재전송
  | 'needs_base'; // 단조 개선: 원본 장비(upgradeFrom)를 갖지 않음
export type TxKind = 'none' | 'buy' | 'equip' | 'focus' | 'recover';
export interface TxResultView {
  /** 결과마다 1 증가(같은 결과를 두 번 처리하지 않게 UI·오디오가 비교한다) */
  readonly seq: number;
  readonly kind: TxKind;
  readonly code: TxCode;
  readonly itemId: string;
  readonly requestId: string;
}
/**
 * 프로필 상태: ok 정상(이 탭이 거래 권한을 가짐) / other_tab 다른 탭·창이 거래 권한을 가짐(전투·보상은 가능, 거래 잠금) /
 * corrupt 저장 손상(원본 백업, 거래 잠금) / future 다음 버전 저장(덮어쓰지 않음) / unavailable 저장소·락 사용 불가
 */
export type ProfileMode = 'ok' | 'other_tab' | 'corrupt' | 'future' | 'unavailable';
export interface ProfileView {
  readonly mode: ProfileMode;
  /** 상태가 바뀔 때마다 1 증가(UI 재그리기 키). 다른 탭 변경을 읽어 들여도 증가한다 */
  readonly revision: number;
  /** 쓸 수 있는 골드 = 저장 확정 획득 + 준비금 - 유효 구매 */
  readonly gold: number;
  /** 아직 저장하지 못한 보상(쓸 수 없다, 저장되면 gold로 옮겨진다) */
  readonly pendingGold: number;
  /** 가진 장비 ItemId(기본 무기 포함). 바뀔 때만 새 배열 */
  readonly owned: readonly string[];
  /** 단조 개선으로 다른 장비가 된 원본 장비 ItemId(owned에 없다, 다시 살 수 없다). 바뀔 때만 새 배열 */
  readonly reforged: readonly string[];
  readonly weapon: string;
  readonly armor: string | null;
  /** 주력 스킬 0(없음)·1..3 */
  readonly skillFocus: number;
  readonly starterGranted: boolean;
  /** 마지막 저장 시도가 성공했는지 */
  readonly lastSaveOk: boolean;
  readonly last: TxResultView;
}
/** 성장 v1(레벨·EXP·스킬 트리, docs/SKILL-TREE.md). 데이터·규칙은 shared/growth.ts */
export type GrowthMode = 'ok' | 'corrupt' | 'future' | 'unavailable';
/** 배우기·초기화·알림 결과 한 건(GrowthView.last). 같은 객체를 재사용하고 seq로 새 결과를 안다 */
export interface GrowthResultView {
  /** 결과마다 1 증가 */
  readonly seq: number;
  /** none 처음·알림, learn 배우기, reset 초기화 */
  readonly kind: 'none' | 'learn' | 'reset' | 'notice';
  readonly code: GrowthCode;
  /** 배우기 노드 인덱스(초기화·알림 -1) */
  readonly node: number;
  readonly requestId: string;
  /** 문구(GROWTH_TEXT). UI는 이 문구를 그대로 보여 준다 */
  readonly message: string;
}
export interface GrowthView {
  readonly mode: GrowthMode;
  /** 바뀔 때마다 1 증가(UI 재그리기 키). 던전 안 EXP 증가에도 오른다 */
  readonly revision: number;
  /** 현재 레벨 1..30(저장 확정 EXP + 이번 판 EXP 기준) */
  readonly level: number;
  /** 누적 EXP(저장 확정 + 이번 판) */
  readonly xp: number;
  /** 이 레벨 시작 누적 EXP / 다음 레벨 누적 EXP(최고 레벨이면 둘 다 XP_MAX) */
  readonly xpLevelStart: number;
  readonly xpNext: number;
  /** 이번 판에서 얻었지만 아직 저장하지 않은 EXP */
  readonly pendingXp: number;
  /** 총 스킬 포인트 / 쓴 포인트(무료 랭크 제외) */
  readonly points: number;
  readonly spent: number;
  /** 노드별 랭크(SKILL_NODES 순서). 마을에서만 바뀐다. 같은 배열을 재사용한다 */
  readonly ranks: Uint8Array;
  readonly lastSaveOk: boolean;
  readonly last: GrowthResultView;
  /** 마지막 레벨업의 시뮬레이션 틱과 새 레벨(-1·0이면 없음). 클리어 보상 레벨업이 장면 전환 뒤에도 연출되게 남긴다 */
  readonly lastLevelUpTick: number;
  readonly lastLevelUpLevel: number;
  /** 이 성장 장부의 영웅(c022: 영웅별 레벨·스킬). 없으면 카엘(옛 빌드·픽스처) */
  readonly hero?: PlayableHeroId;
  /** 환생으로 이어 가는 습득 스킬 비트(레인 R). 없으면 0 */
  readonly kept?: number;
}
/** 실제 전투에 쓰이는 능력치. 마을에서는 장착 즉시, 던전에서는 입장 때 고정된 값 */
export interface StatsView {
  readonly attack: number;
  readonly maxHp: number;
  readonly maxMp: number;
  readonly critChance: number;
  /** 스킬 1..4 재사용 대기(틱). 레벨·스킬 트리(질풍)·주력 스킬 반영 */
  readonly skillCooldown: Float32Array;
  /** 능력치를 만든 장착 갑옷의 외형 단계(카엘 몸 T1~T3·초상의 정본, ECONOMY 14절) */
  readonly armorTier: 1 | 2 | 3;
  /** 능력치를 만든 장착 무기의 외형 단계(칼날 빛의 정본) */
  readonly weaponTier: 1 | 2 | 3;
}
export interface VillageView {
  /** 상호작용 반경 안의 가장 가까운 NPC(shared/rpg VILLAGE_NPCS 인덱스), 없으면 -1 */
  readonly nearNpc: number;
  /** 열린 NPC 창, 없으면 -1. 열려 있는 동안 영웅은 움직이거나 공격하지 않는다 */
  readonly menuNpc: number;
  /** 장비·상태 창이 열려 있는지(NPC 창과 동시에 열리지 않는다). 열려 있는 동안 영웅 입력 차단 */
  readonly inventoryOpen: boolean;
  /** 스테이지 지도 화면이 열려 있는지(S3). 열려 있는 동안 영웅 입력 차단 */
  readonly stageMapOpen: boolean;
  /** 퀘스트 창이 열려 있는지(마을에서만). 열려 있는 동안 영웅 입력 차단 — 전투(던전)에서는 열리지 않는다 */
  readonly questOpen: boolean;
  /** 윤회의 문(마을① 광장, 레인 R) 상호작용 반경 안인가 / 확인 화면이 열렸는가. 옛 픽스처에는 없다 */
  readonly nearGate?: boolean;
  readonly rebirthOpen?: boolean;
}

/** 공격 자석 대상(COMBAT-FEEL 2.3). 렌더·UI가 대상 링을 그리는 데 쓴다 */
export interface MagnetView {
  /** 몹 인덱스, 없으면 -1 */
  readonly target: number;
  /** 보스가 대상인지(그때 target은 -1) */
  readonly boss: boolean;
}

/** 연속 처치(폭주) 상태(COMBAT-FEEL 2.5). HUD는 화면 가장자리에만 그린다 */
export interface RampageView {
  /** 지금 이어진 처치 수. 창이 끝나면 0 */
  readonly chain: number;
  /** 사슬 창 남은 틱(0 = 사슬 없음) */
  readonly timer: number;
  /** 이동 속도 효과 남은 틱 */
  readonly buff: number;
  /** 이번 판 단계 달성 횟수 */
  readonly tiers: number;
  /** 이번 판 최고 사슬 */
  readonly best: number;
  /** 스타일 랭크 0..STYLE_RANK.letters.length(0 = 없음, COMBAT-FEEL 2.14) */
  readonly rank: number;
}

/** 물약 퀵슬롯·비약·부활 깃털 상태(D2c). 게임 game/consumables.ts가 채운다. 보유 수는 GameView.gear.cons */
export interface ConsumeView {
  /** 물약 공용 쿨다운 남은 틱(0 = 준비)과 전체 길이 */
  readonly potionCd: number;
  readonly potionCdMax: number;
  /** 다음 사용의 회복 배율(연속 사용 감쇠, 1 = 감쇠 없음) */
  readonly decay: number;
  /** 자동 물약 문턱(HP 비율, 0 = 끔) */
  readonly autoPotion: number;
  /** 이번 판에 부활 깃털을 썼는지 */
  readonly featherUsed: boolean;
  /** 퀵슬롯이 쓸 물약 id(0 HP, 1 MP — 가진 것 중 가장 큰 것, 없으면 '') */
  readonly slotItem: readonly string[];
  /** 이번 판 물약 사용 수(QA) */
  readonly used: number;
}

/** 레이드 용병 한 명(RAID-PRD 3절). 칸 번호 = MERCS 순서(0 방패병, 1 궁수, 2 사제) */
export interface MercView {
  readonly x: number;
  readonly z: number;
  readonly prevX: number;
  readonly prevZ: number;
  readonly facing: number;
  readonly hp: number;
  readonly maxHp: number;
  /** game/raid MercAct: 0 서기, 1 걷기, 2 공격, 3 시전(치유·도발), 4 쓰러짐 */
  readonly act: number;
  readonly actTick: number;
  /** 쓰러진 뒤 다시 일어나기까지 남은 세계 틱(0 = 살아 있음) */
  readonly down: number;
  readonly hurtTick: number;
  readonly evading: boolean;
  /** 넷 판에서 이 용병 칸이 쓰이는가(사람이 차지한 칸은 false — 렌더·HUD가 숨긴다). 없으면 true */
  readonly visible?: boolean;
}
export interface FireView {
  readonly active: boolean;
  readonly x: number;
  readonly z: number;
  readonly radius: number;
  readonly ttl: number;
  readonly total: number;
}
export interface RaidView {
  readonly active: boolean;
  readonly raid: string;
  /** 보스가 노리는 파티 칸(0 영웅, 1..3 용병, -1 없음) */
  readonly target: number;
  readonly tauntTicks: number;
  readonly phase: number;
  readonly enraged: boolean;
  readonly enrageIn: number;
  readonly air: number;
  /** 지금 패턴(content/raid WyvernPattern, -1 없음) */
  readonly pattern: number;
  readonly weekClears: number;
  readonly weekLimit: number;
  readonly practice: boolean;
  /**
   * 난이도(C13): 'normal' | 'hard'. hardOpen = 하드 해금(1차 환생), hardFirst = 하드 첫 클리어 영수증이 있다(칭호 보임).
   * brandSlots/brandLeft: 하드 잿불 낙인이 붙은 파티 칸 비트(0 영웅, 1..3 용병)와 폭발까지 남은 틱(0 = 없음). 옛 픽스처에는 없다
   */
  readonly diff?: string;
  readonly hardOpen?: boolean;
  readonly hardFirst?: boolean;
  readonly brandSlots?: number;
  readonly brandLeft?: number;
  readonly mercs: readonly MercView[];
  readonly fires: readonly FireView[];
}

export interface GameView {
  // 스칼라 필드는 모두 getter로 살아 있는 값이다(생성 시 복사 금지).
  readonly tick: number;
  /** 몹·보스 시간 배율(각성 모으기 0.2) */
  readonly timeScale: number;
  /** 남은 히트스톱 틱. >0이면 영웅·몹이 멈춘다 */
  readonly hitstop: number;
  /**
   * 직전 step에서 각 영역이 실제로 진행한 틱(0..1). COMBAT-FEEL 2.1 표.
   * heroRate: 영웅 이동·동작(히트스톱·일시정지 0). worldRate: 몹·보스·텔레그래프(각성 0.2/0, 히트스톱 0).
   * 몹·보스 위치는 매 틱 dt*worldRate로 적분한다(5틱마다 몰아서 움직이지 않는다).
   * 렌더 애니메이션 위상 = tick값 - rate*(1-alpha).
   */
  readonly heroRate: number;
  readonly worldRate: number;
  /**
   * 누적 유효 틱(일시정지·히트스톱 동안 멈춘다). 렌더 시계는 이것을 읽는다:
   * 표시 시간 = heroTime - heroRate*(1-alpha), worldTime - worldRate*(1-alpha).
   * 한 렌더 프레임에 여러 틱이 지나도 틱별 배율이 정확히 반영된다(렌더 리뷰 P1).
   */
  readonly heroTime: number;
  readonly worldTime: number;
  readonly map: MapDef;
  readonly grid: MapGrid;
  readonly hero: HeroView;
  readonly mobs: MobsView;
  readonly boss: BossView;
  /** C2 투척형 몹 투사체(shared/mobRoles). 옛 픽스처에는 없다 */
  readonly mobShots?: import('./mobRoles').MobShotsView;
  /** C2: 텔레그래프 슬롯별 출처(TeleSource, telegraphs와 같은 번호). 선택 필드 — 없으면 렌더가 모두 몹 규칙으로 그린다 */
  readonly telegraphSources?: ArrayLike<number>;
  readonly telegraphs: readonly TelegraphSlot[];
  readonly flow: FlowView;
  readonly combo: ComboView;
  readonly link: LinkView;
  readonly magnet: MagnetView;
  readonly rampage: RampageView;
  readonly awaken: AwakenView;
  readonly result: ResultView;
  readonly settings: SettingsView;
  readonly profile: ProfileView;
  readonly growth: GrowthView;
  /**
   * 지금 플레이하는 영웅(c022). 마을에서만 바뀐다(던전 안 전환 금지). 없으면 카엘(옛 픽스처).
   * 렌더는 이것으로 리그·무기·색을, HUD는 스킬 표를 고른다
   */
  readonly heroId?: PlayableHeroId;
  /** 스킬 슬롯 6×2 배치·현재 페이지(D1e). 입력층이 슬롯 키를 스킬 비트로 풀고 HUD가 현재 페이지를 그린다 */
  readonly slots: SlotsView;
  readonly stats: StatsView;
  readonly village: VillageView;
  readonly records: Readonly<Record<RegionId, RegionRecordView>>;
  /** 스테이지 진행(D3a) */
  readonly progress: StageProgressView;
  /** 하드 난이도 진행(D3b). 렌더 픽스처 등 일부 뷰에는 없다 */
  readonly hard?: HardProgressView;
  /** 헬 난이도 진행(C10, dungeonhero.stage.hell.v1). 모양은 하드와 같다(selected는 x… id) */
  readonly hell?: HardProgressView;
  /** 헬 보스 판 「지옥불 각인」 장판(C10, game/hellBrand.ts) */
  readonly hellBrand?: { readonly active: boolean; readonly pools: readonly { readonly active: boolean; readonly x: number; readonly z: number; readonly radius: number; readonly ttl: number; readonly total: number }[] };
  /** 장비 6부위·가방·강화석·보호 주문서·강화 장부(D2a·D2b, dungeonhero.gear.v1) */
  readonly gear: GearView;
  /** 아바타(레인 E, dungeonhero.avatar.v1). 렌더 픽스처 등 일부 뷰에는 없다 */
  readonly avatar?: AvatarView;
  /** 모험 목표판(레인 E 2단계, dungeonhero.adventure.v1). 렌더 픽스처 등 일부 뷰에는 없다 */
  readonly adventure?: AdventureView;
  /** 도감 2.0(C6, dungeonhero.codex.v1): 85칸·점수·보너스. 옛 픽스처에는 없다 */
  readonly codex?: CodexView;
  /** 이번 주 세계 법칙(C10b, 날짜로 정해진다). 옛 픽스처에는 없다 */
  readonly worldRule?: WorldRuleView;
  /** 용사의 시련(3개월 C7, game/trialRun.ts TrialsView). 렌더 픽스처 등 일부 뷰에는 없다 */
  readonly trials?: TrialsView;
  /** 전직 퀘스트 상태(3개월 C10-5, game/jobRun.ts) */
  readonly job?: JobView;
  /** 바닥 드롭 풀(월드 빛기둥·줍기) */
  readonly loot: LootView;
  /** 물약 퀵슬롯·비약·부활 깃털(D2c) */
  readonly consume: ConsumeView;
  /** 스페셜 몬스터(황금 고블린·챔피언) 한 판 상태. 옛 픽스처에는 없다 */
  readonly special?: SpecialView;
  /** 레이드 판 상태(RAID-PRD): 용병·불 장판·위협 대상·페이즈. 옛 픽스처에는 없다 */
  readonly raid?: RaidView;
  /** C8-6 레이드 룩 고정 프레임(?qa 픽스처, game/qaFrame.ts). 고정 중일 때만 값이 있다 — 렌더는 발광 솟음·반복 클립 시각을 이것으로 정한다 */
  readonly qaFrame?: { readonly name: string; readonly surge?: number } | null;
  /** 환생(레인 R, dungeonhero.rebirth.v1): 지금 영웅의 회차·포인트·윤회의 문. 옛 픽스처에는 없다 */
  readonly rebirth?: RebirthView;
  /** 자유 스탯 배분(레인 C, dungeonhero.stats.v1): 지금 영웅·회차의 스탯 포인트·배분. 옛 픽스처에는 없다 */
  readonly statAlloc?: StatView;
  /** 숙련·초월(C4, gear.v1 ms): 숙련 레벨·포인트·배분·부위 초월·이정표. 옛 픽스처에는 없다 */
  readonly mastery?: MasteryView;
  /** ARPG 필드·균열(레인 F). 옛 픽스처에는 없다 */
  readonly field?: FieldView;
  /** 파티 판(레인 D): 동료·웨이브·부활·결과. 옛 픽스처에는 없다 */
  readonly party?: PartyView;
  /** 레이드 4인 넷 판(raidLink): 다른 사람 영웅·부활·결과. 옛 픽스처에는 없다 */
  readonly raidNet?: RaidNetView;
  readonly events: EventQueue;
}

/** UI → 게임 명령(버튼 클릭 등). 시뮬레이션 다음 틱 시작에 처리된다. */
export type GameCommand =
  | { type: 'start' }
  /** 던전 입장. region 생략 = r1(기존 호출 호환). 마을·타이틀에서만 */
  | { type: 'enterDungeon'; region?: RegionId; stage?: string }
  /**
   * 스테이지 입장(D3a): 열린 스테이지만. 마을·타이틀, 또는 보상을 고른 결과 화면·패배 화면에서(다음 스테이지·재도전).
   * 마을 문으로 걸어 들어가면 그 지역의 고른 스테이지(progress.selected)로 이 명령과 같은 경로를 탄다.
   * region만 주는 enterDungeon은 자유 입장(지도 그대로·진행 기록 없음, 기존 시험·QA 하네스 호환)이다
   */
  | { type: 'enterStage'; stage: string }
  /** 마을 문 앞 안내 카드에서 그 지역의 스테이지 고르기(열린 스테이지만, 마을에서만) */
  | { type: 'selectStage'; region: RegionId; index: number; hard?: boolean; hell?: boolean }
  /** 마을 문 난이도 고르기(D3b): 하드면 문으로 들어갈 때 그 지역의 고른 하드 스테이지로 간다(하드 지역이 잠겼으면 노말). 마을에서만 */
  | { type: 'setDifficulty'; hard: boolean; hell?: boolean }
  /** 영웅 선택(c022): 마을에서만(던전·결과·일시정지 거절). 결과는 Ev.HeroSelect */
  | { type: 'selectHero'; hero: PlayableHeroId }
  /** 워프: 도착했던 마을로 이동(마을에서만, 이동 연출) */
  | { type: 'travel'; town: number }
  | { type: 'returnToVillage' }
  /**
   * 레이드 입장(RAID-PRD 1절): 마을(레이드 지휘소 창)·결과·패배 화면에서. 마을② 도착 전이면 거절(Ev.RaidDenied).
   * 결과는 Ev.SceneChange(던전) 또는 Ev.RaidDenied
   */
  | { type: 'enterRaid'; raid: string; /** 'hard' = 하드(C13, 1차 환생 뒤). 없으면 노말 */ diff?: string }
  /** 일일 의뢰 수령(L9): 마을에서만. 보상은 지갑(골드·강화석)에 바로 들어간다 */
  | { type: 'dailyClaim'; quest: string }
  /** 주간 이벤트 목표 수령·마무리 보상(3개월 C5, daily v3 weekly 장부): 마을에서만. goal = liveGoalKey */
  | { type: 'liveClaim'; goal: string }
  | { type: 'liveFinale' }
  /** 일일 던전·무한의 탑 입장(L9): 마을에서만. 횟수·해금은 일일 장부가 판정한다. 결과는 Ev.DailyEnter */
  | { type: 'enterDaily'; kind: 'vault' | 'mine' | 'tower' }
  /** 용사의 시련 입장(3개월 C7): 마을, 또는 시련 판의 결과·패배 화면(다시 도전). trial = content/trials 시련 id */
  | { type: 'enterTrial'; trial: string }
  /** 전직(C10-5): 교관 앞 갈래 선택(0 블러드 버서커·1 소드 마스터)·전직 II 승급 */
  | { type: 'jobChoose'; branch: number }
  | { type: 'jobAdvance' }
  /** 시련 상자 열기(마을): 그 시련판 10개를 모두 이뤘을 때 한 번 */
  | { type: 'claimTrialChest'; board: TrialBoardId }
  /** 레이드 4인 판 「나가기」(일시정지 없음): 연결을 놓고 마을로. 넷 판이 아니면 무시 */
  | { type: 'raidNetLeave' }
  /** 결과·패배 화면에서 방금 한 지역(flow.mapId)을 다시 시작 */
  | { type: 'retry' }
  | { type: 'pickCard'; index: number }
  /** 클리어 연출 대기를 건너뛰고 바로 결과로(대기 0.8초 뒤부터. 그 전·대기 중이 아니면 무시) */
  | { type: 'skipClear' }
  | { type: 'setPaused'; paused: boolean }
  | { type: 'setSetting'; key: keyof SettingsView; value: boolean }
  /** NPC 창 닫기(창 열기는 Btn.Interact 입력: 키 E/Enter, 패드 A, 터치 상호작용 버튼) */
  | { type: 'closeMenu' }
  /** 열린 NPC가 파는 물건 구매. requestId는 UI가 클릭마다 새로 만든다(재전송 중복 방지) */
  | { type: 'buy'; itemId: string; requestId: string }
  /** 가진 장비 장착(itemId null = 갑옷 벗기. 무기는 벗을 수 없다). 마을에서 NPC 창이나 장비 창이 열린 동안만 */
  | { type: 'equip'; slot: ItemSlot; itemId: string | null; requestId: string }
  /** 주력 스킬(0 해제, 1..3). 교관 창이 열린 동안만 */
  | { type: 'setSkillFocus'; skillId: number; requestId: string }
  /** 손상 프로필을 백업 뒤 새로 시작(준비금 재지급 없음). mode === 'corrupt'일 때만 */
  | { type: 'recoverProfile'; requestId: string }
  /** 장비·상태 창(마을 전용, NPC 없이). 열려 있는 동안 영웅 입력 차단 */
  | { type: 'openInventory' }
  | { type: 'openStageMap' }
  /** 퀘스트 창(마을 전용, 다른 창이 없을 때). 열려 있는 동안 영웅 입력 차단 */
  | { type: 'openQuest' }
  /** 스킬 트리 노드 한 랭크 배우기(SKILL_NODES 인덱스). 마을에서 장비 창·교관 창이 열린 동안만 */
  | { type: 'learnSkill'; node: number; requestId: string }
  /** 스킬 트리 초기화(무료, 포인트 전부 돌려받기). 마을에서 장비 창·교관 창이 열린 동안만 */
  | { type: 'resetSkills'; requestId: string }
  /** 스킬 슬롯 페이지 바꾸기(키 Tab·터치 쪽 표시). 쿨·MP·동작에 영향이 없고 언제든 된다. page 생략 = 다음 페이지 */
  | { type: 'setSkillPage'; page?: number }
  /** 스킬 슬롯 배치 바꾸기(0..11번 칸에 스킬 1..5, 0 = 비우기). 마을에서만. 같은 스킬이 다른 칸에 있으면 자리를 맞바꾼다 */
  | { type: 'setSkillSlot'; slot: number; skill: number }
  /**
   * 장비(D2a·D2b). requestId는 UI가 클릭마다 새로 만들고 연산 id가 된다(재전송·재로드 중복 0). 장착·벗기·정리는 마을 가방 창·대장간 창,
   * 강화·보호 주문서 구매는 대장간 창이 열린 동안만. protect = +13~+15 실패 하락을 보호 주문서로 막기(지역6 클리어 뒤)
   */
  | { type: 'gearEquip'; uid: string; requestId: string }
  // 아바타(레인 E): 장착·해제·외형 숨기기(마을에서만)
  | { type: 'avatarEquip'; id: string }
  | { type: 'avatarUnequip'; slot: string }
  | { type: 'avatarHide'; hide: boolean }
  // 아바타 제작(E3, 마을에서만): 조각 + 골드. requestId가 연산 id라 재전송·두 탭이 두 번 만들지 않는다
  | { type: 'avatarCraft'; id: string; requestId: string }
  // 도감 단계 보상 받기(5·10·20종, 마을에서만, 단계마다 한 번)
  | { type: 'adventureClaim'; step: number }
  | { type: 'gearUnequip'; slot: EquipSlot; requestId: string }
  | { type: 'gearEnhance'; uid: string; protect: boolean; requestId: string }
  | { type: 'gearSalvage'; uid: string; requestId: string }
  | { type: 'gearBuyProtect'; requestId: string }
  /** 손상된 장비 저장(주 저장·정상 사본 모두 못 읽음)을 새로 시작. 손상 원문은 .bak에 남긴다. 마을에서 gear.mode === 'corrupt'일 때만(UI 두 단계 확인) */
  | { type: 'gearRecover'; requestId: string }
  /** D2c 상점 구매(물약·귀환서·부활 깃털): 잡화상 창이 열린 동안만. n개 */
  | { type: 'consBuy'; item: string; n: number; requestId: string }
  /** D2c 비약 제작(연금술사, 마을④부터 — 연금술사 NPC 전까지 잡화상 창의 연금 탭) */
  | { type: 'consCraft'; item: string; n: number; requestId: string }
  /** D2c 비약 마시기(마을·던전). item = 비약 id 또는 'all'(가진 계열 모두 한 병씩, "한 번에 사용") */
  | { type: 'useElixir'; item: string; requestId: string }
  /** D2c 귀환서 사용(던전): 바닥 드롭을 모두 거두고 마을로 */
  | { type: 'useReturnScroll'; requestId: string }
  /** D2c 보석 박기·빼기·합성(대장간 보석 탭, 마을③부터 — 보석상 NPC 전까지) */
  | { type: 'gemSocket'; uid: string; idx: number; code: number; requestId: string }
  | { type: 'gemUnsocket'; uid: string; idx: number; requestId: string }
  | { type: 'gemCombine'; code: number; requestId: string }
  /** S7 마을 장비 상점 구매(잡화상·대장간 창, 마을②~④): offer = content/townShop 품목 id. 지금 머문 마을의 품목만 */
  | { type: 'gearBuy'; offer: string; requestId: string }
  /** D2c 자동 물약 문턱(0 = 끔, 0.3·0.5). UI 설정(dungeonhero.ui.v1)이 시작할 때 한 번 보낸다 */
  | { type: 'setAutoPotion'; pct: number }
  /** 환생(레인 R): 윤회의 문 확인 화면이 열린 동안, 지금 영웅을 다음 회차로. requestId가 연산 id(재전송·재로드 멱등) */
  | { type: 'rebirth'; requestId: string }
  /** 환생 포인트 배분(재배분 무료): 교관 창 「환생」 탭 또는 윤회의 문 창이 열린 동안. 합 ≤ 가진 포인트 */
  | { type: 'rebirthAlloc'; alloc: RebirthAlloc; requestId: string }
  /** 새 모드 소개 카드 확인(모드별 진행 기록, 마을에서) */
  | { type: 'rebirthIntro'; requestId: string }
  /** 자유 스탯 배분(레인 C): 지금 영웅·회차의 배분을 이 값으로 맞춘다(초기화 = 모두 0). 마을에서만, 합 ≤ 스탯 포인트 */
  | { type: 'statAlloc'; alloc: StatAlloc; requestId: string }
  /** 숙련 배분(C4): 이 값으로 맞춘다. 마을에서만, 합 ≤ 숙련 포인트, 한 칸이라도 줄이면 재분배 골드 */
  | { type: 'masteryAlloc'; alloc: MasteryAlloc; requestId: string }
  /** 초월 강화 1회(C4): 대장간 창에서, 그 부위 장비 +15 */
  | { type: 'transcend'; slot: EquipSlot; requestId: string }
  /** 숙련 이정표 보상 받기(C4): 마을에서 */
  | { type: 'masteryClaim'; idx: number; requestId: string }
  /** ARPG 필드(레인 F): enter 마을① 필드 문과 같은 입장(마을에서만) · return 마을로(필드·균열) · map 월드 맵 열기/닫기 */
  | { type: 'field'; op: 'enter' | 'return' | 'map' | 'rift' | 'grift' | 'gclose'; tier?: number }
  /** QA 픽스처 전용(?qa 에서만 main.ts가 보낸다). 정상 플레이 증거와 구분해 기록한다 */
  | { type: 'qa'; op: 'fillAwaken' | 'gotoRoom' | 'spawnWave' | 'killAll' | 'bossLow'; arg: number };

/** 입력 녹화·재생 한 틱 */
export interface ReplayFrame {
  readonly tick: number;
  readonly moveX: number;
  readonly moveZ: number;
  readonly held: number;
  readonly pressed: number;
}

/** UI 전용 설정(시뮬레이션과 무관, UI가 localStorage 'dungeonhero.ui.v1'에 직접 저장) */
export interface UiPrefs {
  touchScale: number;
  quality: 'auto' | 'low' | 'mid' | 'high';
  renderer: 'auto' | 'webgl';
  /** 자동 물약 문턱(D2c, 0 = 끔) */
  autoPotion: number;
  /** 터치 버튼 투명도 0.35..1 (UX-FLOW 3절) */
  touchOpacity: number;
  /** 고객이 옮긴 터치 버튼 중심(화면 비율 0..1, id는 skillHudLayout TOUCH_CUSTOM_IDS). 비어 있으면 자동 배치 */
  touchPos: Partial<Record<string, { x: number; y: number }>>;
}

export interface Game {
  readonly view: GameView;
  /** 고정 60Hz. dt는 항상 FIXED_DT. */
  step(dt: number, input: InputFrame): void;
  command(cmd: GameCommand): void;
  /** 미뤄 둔 저장을 쓴다. 고정 스텝 밖(렌더 프레임·pagehide)에서 부른다. */
  flushSaves(): void;
}
